<template>
  <el-form
    v-show="getShow"
    ref="formLogin"
    :model="registerData.registerForm"
    :rules="registerRules"
    class="login-form"
    label-position="top"
    label-width="120px"
    size="large"
  >
    <el-row class="mx-[-10px]">
      <el-col :span="24" class="px-10px">
        <el-form-item>
          <LoginFormTitle class="w-full" />
        </el-form-item>
      </el-col>
      <el-col :span="24" class="px-10px">
        <el-form-item v-if="registerData.tenantEnable === 'true'" prop="tenantName">
          <el-input
            v-model="registerData.registerForm.tenantName"
            :placeholder="t('login.tenantname')"
            :prefix-icon="iconHouse"
            :disabled="tenantLockedByWebsite"
            link
            type="primary"
            size="large"
          />
        </el-form-item>
      </el-col>
      <el-col :span="24" class="px-10px">
        <el-form-item prop="username">
          <el-input
            v-model="registerData.registerForm.username"
            placeholder="手机号"
            size="large"
            :prefix-icon="iconAvatar"
          />
        </el-form-item>
      </el-col>
      <el-col :span="24" class="px-10px">
        <el-form-item prop="nickname">
          <el-input
            v-model="registerData.registerForm.nickname"
            placeholder="昵称"
            size="large"
            :prefix-icon="iconAvatar"
          />
        </el-form-item>
      </el-col>
      <el-col :span="24" class="px-10px">
        <el-form-item prop="password">
          <el-input
            v-model="registerData.registerForm.password"
            type="password"
            auto-complete="off"
            :placeholder="t('login.password')"
            size="large"
            :prefix-icon="iconLock"
            show-password
          />
        </el-form-item>
      </el-col>
      <el-col :span="24" class="px-10px">
        <el-form-item prop="confirmPassword">
          <el-input
            v-model="registerData.registerForm.confirmPassword"
            type="password"
            size="large"
            auto-complete="off"
            :placeholder="t('login.checkPassword')"
            :prefix-icon="iconLock"
            show-password
          />
        </el-form-item>
      </el-col>
      <el-col :span="24" class="px-10px">
        <el-form-item>
          <el-button
            :loading="loginLoading"
            :disabled="loginLoading || registrationSucceeded"
            class="w-full"
            type="primary"
            @click="getCode()"
          >
            {{ registrationSucceeded ? '注册成功' : t('login.register') }}
          </el-button>
        </el-form-item>
      </el-col>
      <Verify
        v-if="registerData.captchaEnable === 'true'"
        ref="verify"
        :captchaType="captchaType"
        :imgSize="{ width: '400px', height: '200px' }"
        mode="pop"
        @success="handleRegister"
      />
    </el-row>
    <el-button
      class="w-full"
      :disabled="loginLoading || registrationSucceeded"
      @click="handleBackLogin()"
    >
      {{ t('login.hasUser') }}
    </el-button>
  </el-form>
</template>
<script lang="ts" setup>
import { ElMessage } from 'element-plus'
import LoginFormTitle from './LoginFormTitle.vue'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { useIcon } from '@/hooks/web/useIcon'
import * as authUtil from '@/utils/auth'
import { usePermissionStore } from '@/store/modules/permission'
import * as LoginApi from '@/api/login'
import { parseRouteLocation } from '@/utils/routeParams'
import { LoginStateEnum, useLoginState, useFormValid } from './useLogin'

defineOptions({ name: 'RegisterForm' })

const { t } = useI18n()
const iconHouse = useIcon({ icon: 'ep:house' })
const iconAvatar = useIcon({ icon: 'ep:avatar' })
const iconLock = useIcon({ icon: 'ep:lock' })
const formLogin = ref()
const { validForm } = useFormValid(formLogin)
const { handleBackLogin, getLoginState } = useLoginState()
const { currentRoute, push } = useRouter()
const permissionStore = usePermissionStore()
const redirect = ref<string>('')
const loginLoading = ref(false)
const registrationSucceeded = ref(false)
const tenantLockedByWebsite = ref(false)
const verify = ref()
const captchaType = ref('blockPuzzle') // blockPuzzle 滑块 clickWord 点击文字 pictureWord 文字验证码

const getShow = computed(() => unref(getLoginState) === LoginStateEnum.REGISTER)

const equalToPassword = (_rule, value, callback) => {
  if (registerData.registerForm.password !== value) {
    callback(new Error('两次输入的密码不一致'))
  } else {
    callback()
  }
}

const registerRules = {
  tenantName: [
    { required: true, trigger: 'blur', message: '请输入您所属的租户' },
    { min: 2, max: 20, message: '租户账号长度必须介于 2 和 20 之间', trigger: 'blur' }
  ],
  username: [
    { required: true, trigger: 'blur', message: '请输入手机号' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的 11 位手机号', trigger: 'blur' }
  ],
  nickname: [
    { required: true, trigger: 'blur', message: '请输入您的昵称' },
    { min: 0, max: 30, message: '昵称长度必须介于 0 和 30 之间', trigger: 'blur' }
  ],
  password: [
    { required: true, trigger: 'blur', message: '请输入您的密码' },
    { min: 5, max: 16, message: '用户密码长度必须介于 5 和 16 之间', trigger: 'blur' },
    { pattern: /^[^<>"'|\\]+$/, message: '不能包含非法字符：< > " \' \\\ |', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, trigger: 'blur', message: '请再次输入您的密码' },
    { required: true, validator: equalToPassword, trigger: 'blur' }
  ]
}

const registerData = reactive({
  isShowPassword: false,
  captchaEnable: import.meta.env.VITE_APP_CAPTCHA_ENABLE,
  tenantEnable: import.meta.env.VITE_APP_TENANT_ENABLE,
  registerForm: {
    tenantName: import.meta.env.VITE_APP_DEFAULT_LOGIN_TENANT || '',
    nickname: '',
    tenantId: 0,
    username: '',
    password: '',
    confirmPassword: '',
    captchaVerification: ''
  }
})

const getPcTenantId = () => {
  const tenantId = String(currentRoute.value.query.tenantId || '')
  return /^\d{1,18}$/.test(tenantId) ? tenantId : ''
}

const getPcCallback = () => {
  const state = String(currentRoute.value.query.state || '')
  const redirectUri = String(currentRoute.value.query.redirect_uri || '')
  if (!state && !redirectUri) return null
  if (!/^[A-Za-z0-9_-]{32,128}$/.test(state) || redirectUri !== 'myapp://callback') {
    throw new Error('PC 客户端回调参数无效，请从客户端重新发起注册')
  }
  return { state, redirectUri }
}

const authorizePcClient = async (state: string, redirectUri: string) => {
  const callbackUrl = await LoginApi.authorizePcMember(state)
  if (!callbackUrl || !String(callbackUrl).startsWith('myapp://callback?')) {
    throw new Error('PC 客户端授权失败，请重试')
  }
  const callback = new URL(String(callbackUrl))
  const loopbackCallback = String(currentRoute.value.query.pc_callback || '')
  if (/^http:\/\/127\.0\.0\.1:\d+\/registration\/callback$/.test(loopbackCallback)) {
    try {
      const response = await fetch(loopbackCallback, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: callback.searchParams.get('code'),
          state: callback.searchParams.get('state')
        }),
        signal: AbortSignal.timeout(3000)
      })
      if (response.ok && (await response.json()).ok === true) return
    } catch {
      // 本地回调不可用时回退到正式版 URL Scheme。
    }
  }
  window.location.href = callback.toString()
}

// 提交注册
const handleRegister = async (params: any) => {
  if (loginLoading.value) return
  loginLoading.value = true
  let registrationCompleted = false
  try {
    if (registerData.tenantEnable === 'true') {
      await getTenantId()
      registerData.registerForm.tenantId = authUtil.getTenantId()
    }

    if (registerData.captchaEnable === 'true') {
      registerData.registerForm.captchaVerification = params.captchaVerification
    }

    const data = await validForm()
    if (!data) {
      return
    }

    const res = await LoginApi.memberRegister({
      mobile: registerData.registerForm.username,
      nickname: registerData.registerForm.nickname,
      password: registerData.registerForm.password
    })
    if (!res) {
      return
    }
    authUtil.removeLoginForm()
    authUtil.setAdminToken(res)
    registrationCompleted = true
    registrationSucceeded.value = true
    ElMessage.success({ message: '注册成功，正在自动登录…', duration: 2200 })

    const pcCallback = getPcCallback()
    if (pcCallback) {
      try {
        await authorizePcClient(pcCallback.state, pcCallback.redirectUri)
        await new Promise((resolve) => setTimeout(resolve, 350))
        await push('/home')
      } catch (error) {
        ElMessage.warning(
          `账号已注册并登录，但未能唤起 PC 客户端：${error instanceof Error ? error.message : '请重试'}`
        )
        await push('/home')
      }
      return
    }
    if (!redirect.value) {
      redirect.value = '/portal'
    }
    // 判断是否为SSO登录
    if (redirect.value.indexOf('sso') !== -1) {
      window.location.href = window.location.href.replace('/portal?redirect=', '')
    } else {
      const target = redirect.value || permissionStore.addRouters[0].path
      await push(parseRouteLocation(target))
    }
  } catch (error) {
    if (!registrationCompleted) {
      ElMessage.error(error instanceof Error ? error.message : '注册失败，请重试')
    }
  } finally {
    loginLoading.value = false
  }
}

// 获取验证码
const getCode = async () => {
  if (loginLoading.value || registrationSucceeded.value) return
  // 情况一，未开启：则直接注册
  if (registerData.captchaEnable === 'false') {
    await handleRegister({})
  } else {
    // 情况二，已开启：则展示验证码；只有完成验证码的情况，才进行注册
    // 弹出验证码
    verify.value.show()
  }
}

// 获取租户 ID
const getTenantId = async () => {
  if (registerData.tenantEnable === 'true') {
    const pcTenantId = getPcTenantId()
    if (pcTenantId) {
      authUtil.setTenantId(Number(pcTenantId))
      return
    }
    const res = await LoginApi.getTenantIdByName(registerData.registerForm.tenantName)
    authUtil.setTenantId(res)
  }
}

// 根据域名，获得租户信息
const getTenantByWebsite = async () => {
  if (registerData.tenantEnable === 'true') {
    const website = location.host
    const res = await LoginApi.getTenantByWebsite(website)
    if (res) {
      registerData.registerForm.tenantName = res.name
      authUtil.setTenantId(res.id)
      tenantLockedByWebsite.value = true
    }
  }
}

watch(
  () => currentRoute.value,
  (route: RouteLocationNormalizedLoaded) => {
    redirect.value = route?.query?.redirect as string
  },
  {
    immediate: true
  }
)
onMounted(() => {
  const pcTenantId = getPcTenantId()
  if (pcTenantId) {
    authUtil.setTenantId(Number(pcTenantId))
    registerData.registerForm.tenantId = Number(pcTenantId)
  } else {
    getTenantByWebsite().catch(() => {
      // 域名识别只是便利能力，失败时保留默认租户，不能阻塞登录或注册表单。
    })
  }
})
</script>

<style lang="scss" scoped>
:deep(.anticon) {
  &:hover {
    color: var(--el-color-primary) !important;
  }
}

.login-code {
  float: right;
  width: 100%;
  height: 38px;

  img {
    width: 100%;
    height: auto;
    max-width: 100px;
    vertical-align: middle;
    cursor: pointer;
  }
}
</style>
